/**
 * screen-cards.tsx — screen-specific card components
 *
 * Imports Button from $lib/ui (ui.tsx). This is safe: ui.tsx re-exports
 * screen-cards at its very end, after Button is defined, so there is no
 * circular-eval failure — Button is resolved before screen-cards is loaded.
 */

import type { ComponentType, ReactNode } from 'react';

import { cn } from '$lib/utils';
import { Button } from '$lib/ui';

// ── MoneyBreakdown ─────────────────────────────────────────────────────────

export function MoneyBreakdown({
	rows
}: {
	rows: { label: string; amount: string; negative?: boolean }[];
}) {
	return (
		<div className="rounded-card border border-border bg-card p-5 shadow-card">
			<p className="mb-2 text-[15px] font-bold">지급/공제 내역</p>
			<dl className="divide-y divide-border">
				{rows.map((r) => (
					<div key={r.label} className="flex items-center justify-between py-3">
						<dt className="text-sm text-foreground">{r.label}</dt>
						<dd
							className={cn(
								'text-sm font-medium tabular-nums',
								r.negative ? 'text-destructive' : 'text-foreground'
							)}
						>
							{r.amount}
						</dd>
					</div>
				))}
			</dl>
		</div>
	);
}

// ── PriorityApprovalCard ───────────────────────────────────────────────────

export function PriorityApprovalCard({
	chip,
	title,
	requester,
	metaLines,
	onApprove,
	onReject
}: {
	chip?: ReactNode;
	title: string;
	requester: string;
	metaLines: string[];
	onApprove?: () => void;
	onReject?: () => void;
}) {
	return (
		<div className="relative overflow-hidden rounded-card border border-border bg-card p-5 pl-6 shadow-card">
			<span className="absolute left-0 top-0 h-full w-1.5 bg-warning" />
			{chip ? <div className="mb-3">{chip}</div> : null}
			<h3 className="text-[20px] font-bold">{title}</h3>
			<p className="mt-1 text-xs text-muted-foreground">{requester}</p>
			<div className="mt-3 rounded-md bg-muted/50 px-3.5 py-2.5 text-sm">
				{metaLines.map((l, i) => (
					<p key={i}>{l}</p>
				))}
			</div>
			{onApprove && onReject ? (
				<div className="mt-4 grid grid-cols-2 gap-3">
					<Button variant="destructive" onClick={onReject}>반려</Button>
					<Button onClick={onApprove}>승인</Button>
				</div>
			) : null}
		</div>
	);
}

// ── DangerCard ─────────────────────────────────────────────────────────────

export function DangerCard({
	title,
	description,
	action
}: {
	title: string;
	description: string;
	action: ReactNode;
}) {
	return (
		<div className="rounded-card border border-destructive/20 bg-chip-red-bg p-5">
			<p className="text-[15px] font-bold text-destructive">{title}</p>
			<p className="mt-2 text-xs text-destructive/80">{description}</p>
			<div className="mt-4 flex justify-end">{action}</div>
		</div>
	);
}

// ── LocationCard ───────────────────────────────────────────────────────────

const locationConfig = {
	verified: {
		label: '위치 확인됨',
		titleClass: 'text-chip-green-fg',
		accentClass: 'bg-chip-green-bg',
		dotClass: 'bg-chip-green-fg'
	},
	out: {
		label: '범위 벗어남',
		titleClass: 'text-destructive',
		accentClass: 'bg-chip-red-bg',
		dotClass: 'bg-destructive'
	},
	locating: {
		label: '위치 확인 중',
		titleClass: 'text-muted-foreground',
		accentClass: 'bg-muted/40',
		dotClass: 'bg-muted-foreground'
	}
} as const;

export function LocationCard({
	status,
	distanceLabel,
	children
}: {
	status: 'verified' | 'out' | 'locating';
	distanceLabel?: string;
	children?: ReactNode;
}) {
	const config = locationConfig[status];
	return (
		<div className={cn('overflow-hidden rounded-card border border-border shadow-card', config.accentClass)}>
			{/* Map grid placeholder */}
			<div className="relative flex h-28 w-full items-center justify-center overflow-hidden bg-muted/30">
				{/* Horizontal divider lines */}
				<div className="absolute inset-0 flex flex-col justify-evenly">
					{[0, 1, 2].map((i) => (
						<div key={i} className="h-px w-full bg-border/60" />
					))}
				</div>
				{/* Vertical divider lines */}
				<div className="absolute inset-0 flex flex-row justify-evenly">
					{[0, 1, 2, 3].map((i) => (
						<div key={i} className="h-full w-px bg-border/60" />
					))}
				</div>
				{/* Center pin */}
				<div className="relative z-10 flex flex-col items-center gap-1">
					<div
						className={cn(
							'size-4 rounded-full ring-2 ring-white',
							config.dotClass,
							status === 'locating' && 'animate-pulse'
						)}
					/>
					<div className="h-3 w-0.5 rounded-full bg-foreground/30" />
				</div>
			</div>
			{/* Info row */}
			<div className="flex items-center justify-between px-4 py-3">
				<p className={cn('text-sm font-bold', config.titleClass)}>{config.label}</p>
				{distanceLabel ? (
					<p className="text-xs text-muted-foreground">{distanceLabel}</p>
				) : null}
			</div>
			{children ? <div className="px-4 pb-3">{children}</div> : null}
		</div>
	);
}

// ── AttendanceActionCard ───────────────────────────────────────────────────

export function AttendanceActionCard({
	statusChip,
	time,
	hint,
	action
}: {
	statusChip: ReactNode;
	time: string;
	hint: string;
	action: ReactNode;
}) {
	return (
		<div className="rounded-card border border-border bg-card p-5 shadow-card">
			<div className="flex items-start justify-between gap-3">
				<div className="shrink-0">{statusChip}</div>
			</div>
			<p className="mt-3 text-5xl font-bold tabular-nums text-foreground">{time}</p>
			{hint ? <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p> : null}
			<div className="mt-4 flex justify-end">{action}</div>
		</div>
	);
}

// ── SettingToggleRow ───────────────────────────────────────────────────────

export function SettingToggleRow({
	icon: Icon,
	title,
	subtitle,
	state
}: {
	icon: ComponentType<{ className?: string }>;
	title: string;
	subtitle?: string;
	state: ReactNode;
}) {
	return (
		<div className="flex min-h-14 items-center gap-3 rounded-card border border-border bg-card px-4 py-3 shadow-card">
			<span className="grid size-9 shrink-0 place-items-center rounded-full bg-muted">
				<Icon className="size-4 text-foreground" />
			</span>
			<span className="min-w-0 flex-1">
				<span className="block text-sm font-bold text-foreground">{title}</span>
				{subtitle ? (
					<span className="mt-0.5 block text-[11px] text-muted-foreground">{subtitle}</span>
				) : null}
			</span>
			<span className="shrink-0">{state}</span>
		</div>
	);
}
